import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useLayoutEffect} from "react";
import {Navigate, useNavigate} from "react-router-dom";
import {unpauseSubscription} from "@/api/billing";
import {ApiError} from "@/api/client";
import {useCabinetData} from "@/hooks/useCabinetData";
import {formatDate} from "@/lib/cabinet";

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
};

export function CabinetSubscriptionUnfreezePage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {subscription, isLoading, isError, refetch} = useCabinetData();

    useLayoutEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: "auto"});
    }, []);

    const unpauseMutation = useMutation({
        mutationFn: unpauseSubscription,
        onSuccess: ({subscription: resumedSubscription}) => {
            void queryClient.invalidateQueries({queryKey: ["cabinet"]});
            navigate("/cabinet/subscription/unfrozen", {
                replace: true,
                state: {
                    confirmed: true,
                    subscription: resumedSubscription,
                },
            });
        },
        onError: (error) => {
            if (error instanceof ApiError && error.code === "subscription_not_paused") {
                void queryClient.invalidateQueries({queryKey: ["cabinet"]});
                navigate("/cabinet", {replace: true});
            }
        },
    });

    if (isLoading) {
        return (
            <div className="cabinet-state">
                <h2>Проверяем подписку</h2>
                <p>Уточняем срок паузы и дату следующей оплаты.</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="cabinet-state">
                <h2>Не удалось загрузить подписку</h2>
                <button type="button" className="btn btn-primary" onClick={() => void refetch()}>
                    Повторить
                </button>
            </div>
        );
    }

    if (!subscription) {
        return <Navigate to="/subscribe/" replace/>;
    }

    if (subscription.status !== "PAUSED") {
        return <Navigate to="/cabinet" replace/>;
    }

    const pauseUntil = formatDate(subscription.pause_until, DATE_FORMAT);
    const plannedChargeAt = formatDate(subscription.next_charge_at, DATE_FORMAT);
    const errorMessage = unpauseMutation.error instanceof ApiError
        ? unpauseMutation.error.message
        : "Не удалось возобновить подписку. Попробуйте ещё раз.";

    return (
        <section className="cabinet-unfreeze-flow" aria-labelledby="unfreeze-title">
            <article className="cabinet-panel cabinet-unfreeze-card">
                <div className="cabinet-unfreeze-visual" aria-hidden="true">
                    <span className="cabinet-unfreeze-orbit cabinet-unfreeze-orbit-one"/>
                    <span className="cabinet-unfreeze-orbit cabinet-unfreeze-orbit-two"/>
                    <span className="cabinet-unfreeze-snowflake">
                        <svg viewBox="0 0 64 64">
                            <path d="M32 11v42M14 21l36 22M14 43l36-22M25 15l7 7 7-7M25 49l7-7 7 7M15 29l10-3-2-10M49 35l-10 3 2 10M15 35l10 3-2 10M49 29l-10-3 2-10"/>
                        </svg>
                    </span>
                    <span className="cabinet-unfreeze-play">
                        <svg viewBox="0 0 24 24"><path d="m9 7 8 5-8 5Z"/></svg>
                    </span>
                </div>

                <div className="cabinet-unfreeze-copy">
                    <span className="cabinet-unfreeze-eyebrow">
                        <i aria-hidden="true"/>
                        Подписка приостановлена
                    </span>
                    <h2 id="unfreeze-title">Готовы вернуться в работу?</h2>
                    <p>
                        Возобновите подписку, чтобы сразу вернуть доступ
                        к кампаниям, CRM и AI-агенту. Все данные и настройки уже на месте.
                    </p>
                </div>

                <div className="cabinet-unfreeze-status" aria-label="Статус паузы">
                    <div>
                        <span>Пауза до</span>
                        <strong>
                            {pauseUntil ? (
                                <time dateTime={subscription.pause_until ?? undefined}>{pauseUntil}</time>
                            ) : "Автоматического возобновления"}
                        </strong>
                        <small>Если ничего не менять, доступ вернётся сам.</small>
                    </div>
                    <div>
                        <span>Сейчас на паузе</span>
                        <ul>
                            <li>Кампании и рассылки</li>
                            <li>CRM и диалоги</li>
                            <li>AI-агент</li>
                        </ul>
                    </div>
                </div>

                <div className="cabinet-unfreeze-note">
                    <span aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                            <path d="M12 3 5 6v5c0 4.6 2.8 8.2 7 10 4.2-1.8 7-5.4 7-10V6Z"/>
                            <path d="M12 8v5M12 16h.01"/>
                        </svg>
                    </span>
                    <div>
                        <strong>Сейчас списания не будет</strong>
                        <p>
                            {plannedChargeAt ? (
                                <>Сейчас оплата запланирована на <time dateTime={subscription.next_charge_at ?? undefined}>{plannedChargeAt}</time>. При раннем возобновлении дата пересчитается.</>
                            ) : "Дата следующей оплаты пересчитается автоматически."}
                        </p>
                    </div>
                </div>

                {unpauseMutation.isError ? (
                    <p className="cabinet-message error cabinet-unfreeze-error" role="alert">
                        {errorMessage}
                    </p>
                ) : null}

                <button
                    type="button"
                    className="btn btn-primary cabinet-unfreeze-action"
                    disabled={unpauseMutation.isPending}
                    aria-busy={unpauseMutation.isPending}
                    onClick={() => unpauseMutation.mutate()}
                >
                    <span>{unpauseMutation.isPending ? "Возобновляем..." : "Возобновить подписку"}</span>
                    {!unpauseMutation.isPending ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 7 8 5-8 5Z"/></svg>
                    ) : null}
                </button>
            </article>
        </section>
    );
}
